import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { DISABLE_TEST_REPORT } from 'global/constants';
import { setError } from 'store/error';
import DigitalSATInstruction from './Instruction';
import DigitalSATModule from './Module';
import DigitalSATBreak from './Break';
import './index.css';

function DigitalSAT() {
    const [isLoading, setIsLoading] = useState(true);
    const [instruction, setInstruction] = useState(true);
    const [testName, setTestName] = useState('');
    const [testReview, setTestReview] = useState(true);
    const [moduleId, setModuleId] = useState('');
    const [moduleName, setModuleName] = useState('');
    const [moduleNumber, setModuleNumber] = useState(0);
    const [moduleSubject, setModuleSubject] = useState(''); 
    const [modulePrompt, setModulePrompt] = useState('');
    const [breakAfter, setBreakAfter] = useState(false);
    const [moduleBreak, setModuleBreak] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [moduleDuration, setModuleDuration] = useState(null);
    const [moduleTime, setModuleTime] = useState(null);
    const [questionTime, setQuestionTime] = useState(null);

    const { user } = useAppSelector(state => state.user);
    const { organization } = useAppSelector(state => state.organization);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { testId } = useParams();

    const getNextModule = () => {
        setIsLoading(true);
        setModuleDuration(null);
        axios.get(`tests/digital/sat/${testId}/`)
            .then(res => {
                if (res.data.id) {
                    setTestName(res.data.testName);
                    setTestReview(res.data.testReview);
                    setModuleId(res.data.id);
                    setModuleName(res.data.name);
                    setModuleNumber(res.data.number);
                    setModuleSubject(res.data.subject);
                    setModuleDuration(res.data.duration);
                    setModuleTime(res.data.duration);
                    setModulePrompt(res.data.prompt);
                    setModuleBreak(false);
                    setBreakAfter(res.data.breakAfter);
                    setQuestions(res.data.questions);
                    setAnswers(res.data.answers);
                    setQuestionIndex(0);
                    setQuestionTime(0);

                    if (sessionStorage.getItem('autosave')) {
                        const autosave = JSON.parse(sessionStorage.getItem('autosave'));
                        if (autosave.moduleId === res.data.id) {
                            setAnswers(autosave.answers);

                            setModuleDuration(autosave.moduleTime);
                        }
                    }

                    setIsLoading(false);
                }
                else {
                    if (!testReview || DISABLE_TEST_REPORT.includes(organization.alias)) {
                        navigate('/tests/digital-sat');
                    }
                    else {
                        navigate(`/report/digital-sat/${testId}`);
                    }
                    
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    const sendReport = () => {
        axios.post('debug/', {
            email: user.email,
            message: JSON.stringify({
                module: `${testName} ${moduleName}`,
                answers: answers,
                moduleDuration: moduleDuration,
                moduleTime: moduleTime
            })
        });
    }

    const beginTest = () => {
        setInstruction(false);
        setQuestionTime(0);
        window.scrollTo(0, 0);
    }

    const autoSave = () => {
        sessionStorage.setItem('autosave', JSON.stringify({
            moduleId: moduleId,
            answers: answers,
            moduleTime: moduleTime
        }));
    }

    const navigateQuestion = (index: number) => {
        if (questionIndex >= 0 && questionIndex < questions.length) {
            let newAnswers = [...answers];
            newAnswers[questionIndex].timeSpent += questionTime;
            setAnswers(newAnswers);
        }
        setQuestionTime(0);
        setQuestionIndex(index);

        autoSave();
    }

    const setAnswer = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answer;
        setAnswers(newAnswers);

        autoSave();
    }

    const setChoice = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answers[questionIndex].answer == answer
            ? ''
            : answer;

        newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== answer);
        setAnswers(newAnswers);

        autoSave();
    }

    const markQuestion = () => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].marked = !answers[questionIndex].marked;
        setAnswers(newAnswers);

        autoSave();
    }

    const crossOutChoice = (choiceId: string) => {
        let newAnswers = [...answers];
        if (newAnswers[questionIndex].crossedOut.includes(choiceId)) {
            newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== choiceId);
        }
        else {
            newAnswers[questionIndex].crossedOut.push(choiceId);
        }
        if (choiceId === answers[questionIndex].answer) {
            newAnswers[questionIndex].answer = '';
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const saveAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations[index] = annotation;
        }
        else {
            const coveredAnnotations = answers[questionIndex].annotations.filter(existing => (
                existing.passageId === annotation.passageId &&
                existing.startIndex >= annotation.startIndex &&
                existing.endIndex <= annotation.endIndex
            ));

            newAnswers[questionIndex].annotations.push(annotation);

            for (let i = 0; i < coveredAnnotations.length; i++) {
                const coveredIndex = newAnswers[questionIndex].annotations.findIndex(existing => (
                    existing.passageId === coveredAnnotations[i].passageId &&
                    existing.startIndex === coveredAnnotations[i].startIndex &&
                    existing.endIndex === coveredAnnotations[i].endIndex
                ));

                const coveredAnnotation = newAnswers[questionIndex].annotations.splice(coveredIndex, 1)[0];
                newAnswers[questionIndex].annotations.push(coveredAnnotation);
            }
        }
        setAnswers(newAnswers);
        autoSave();
    }

    const deleteAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations.splice(index, 1);
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const handleSubmit = () => {
        setIsLoading(true);
        sendReport();
        axios.post(`tests/digital/submit/sat/${testId}/${moduleId}/`, {
                answers: answers,
                timeLeft: moduleTime
            })
            .then(() => {
                if (breakAfter) {
                    setBreakAfter(false);
                    setModuleBreak(true);
                    setModuleTime(null);
                    setQuestionTime(null);
                    setIsLoading(false);
                }
                else {
                    getNextModule();
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    const handleSaveAndExit = () => {
        setIsLoading(true);
        axios.post(`tests/digital/save/sat/${testId}/${moduleId}/`, {
                answers: answers,
                timeLeft: moduleTime
            })
            .then(() => {
                // exit
                navigate(-1);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }
    
    const handleDiscardAndExit = () => {
        sessionStorage.removeItem('autosave');
        navigate(-1);
    }

    useEffect(() => {
        getNextModule();
    }, []);

    useEffect(() => {
        if (!instruction && moduleDuration) setModuleTime(moduleDuration);
    }, [instruction, moduleDuration]);

    useEffect(() => {
        if (moduleTime === null) return;

        if (moduleTime <= 0) {
            handleSubmit();
            return;
        }
        
        const moduleTimer = setInterval(() => {
            setModuleTime(moduleTime - 1);
        }, 1000);

        return () => clearInterval(moduleTimer);
    }, [moduleTime]);

    useEffect(() => {
        if (questionTime === null) return;

        const questionTimer = setInterval(() => {
            setQuestionTime(questionTime + 1);
        }, 1000);

        return () => clearInterval(questionTimer);
    }, [questionTime]);

    if (isLoading) return <Loader />;

    return (
        <div className='digital' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    {testName ? testName : 'Digital SAT Practice Test'} - {organization.name}
                </title>
            </Helmet>
            <div className={moduleNumber && moduleBreak
                ? 'digital__box--break'
                : 'digital__box'
            }>
                {instruction
                ?   <DigitalSATInstruction
                        testName={testName}
                        beginTest={beginTest}
                    />
                :   (moduleBreak
                    ?   <DigitalSATBreak
                            endBreak={getNextModule}
                        />
                    :   <DigitalSATModule
                            moduleName={moduleName}
                            subject={moduleSubject}
                            timeLeft={moduleTime}
                            prompt={modulePrompt}
                            questions={questions}
                            questionIndex={questionIndex}
                            answers={answers}
                            markQuestion={markQuestion}
                            crossOutChoice={crossOutChoice}
                            saveAnnotation={saveAnnotation}
                            deleteAnnotation={deleteAnnotation}
                            handleChange={value => setAnswer(value)}
                            handleSelect={value => setChoice(value)}
                            navigateQuestion={navigateQuestion}
                            handleSaveAndExit={handleSaveAndExit}
                            handleDiscardAndExit={handleDiscardAndExit}
                            handleSubmit={handleSubmit}
                        />
                    )
                }
            </div>
        </div>
    )
}

export default DigitalSAT;